// Dojo Signature — large semi-transparent kanji watermark + season-specific
// glyph SVG (torii gate, cherry blossom, maple leaf, mt. Fuji, broken circle).
// Sits behind every page at z-index 1, way under the content (z >= 10) and the
// ambient particle layer (z = 5). Pointer-events disabled so it never blocks
// interaction.

import { useEffect, useState } from "react";
import { getStoredThemeId, getThemeById } from "@/lib/dojoThemes";

function GlyphSvg({ kind, color }) {
  // All glyphs are designed in a 200x200 box and rendered at currentColor so
  // we only have to pass the theme accent in. Stroke-only minimal line art
  // keeps them ghost-like without dominating the background.
  const stroke = { stroke: color, strokeWidth: 1.6, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  const fill = { fill: color, stroke: "none" };

  switch (kind) {
    case "torii": // Shadow Dojo — torii gate (sacred archway)
      return (
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <g {...stroke}>
            <path d="M30 60 Q100 50 170 60 L170 70 Q100 60 30 70 Z" fill={color} fillOpacity="0.7" />
            <path d="M40 75 L160 75" />
            <path d="M55 75 L55 175" strokeWidth="3" />
            <path d="M145 75 L145 175" strokeWidth="3" />
            <path d="M70 95 L130 95" />
          </g>
        </svg>
      );
    case "blossom": // Sakura — five-petal cherry blossom
      return (
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <g transform="translate(100 100)" {...fill} fillOpacity="0.9">
            {[0, 72, 144, 216, 288].map((rot) => (
              <path key={rot} transform={`rotate(${rot})`}
                d="M0 -60 Q22 -50 22 -22 Q22 -2 0 0 Q-22 -2 -22 -22 Q-22 -50 0 -60 Z" />
            ))}
            <circle r="10" fill={color} fillOpacity="0.6" />
          </g>
        </svg>
      );
    case "maple": // Momiji — seven-point maple leaf
      return (
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <path
            d="M100 25 L112 55 L138 40 L130 70 L160 65 L142 90 L168 105 L138 115 L150 145 L120 130 L115 162 L100 138 L85 162 L80 130 L50 145 L62 115 L32 105 L58 90 L40 65 L70 70 L62 40 L88 55 Z"
            {...fill} fillOpacity="0.92" />
          <path d="M100 100 L100 175" {...stroke} strokeWidth="2" />
        </svg>
      );
    case "fuji": // Yuki — Mt. Fuji silhouette with snow cap
      return (
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <path d="M20 160 L80 70 Q90 56 100 56 Q110 56 120 70 L180 160 Z"
            fill={color} fillOpacity="0.55" />
          <path d="M82 80 L88 70 Q94 60 100 60 Q106 60 112 70 L118 80
                   L114 78 L110 84 L106 80 L100 86 L94 80 L90 84 L86 78 Z"
            fill="#FFFFFF" fillOpacity="0.85" />
          <path d="M20 165 Q60 158 100 162 Q140 166 180 162"
            stroke={color} strokeWidth="1.2" fill="none" opacity="0.5" />
          <path d="M30 175 Q70 170 105 173 Q145 178 175 172"
            stroke={color} strokeWidth="1" fill="none" opacity="0.35" />
        </svg>
      );
    case "circle-broken": // Kintsugi — Enso (zen circle) with golden cracks
      return (
        <svg viewBox="0 0 200 200" width="100%" height="100%">
          <circle cx="100" cy="100" r="68" {...stroke} strokeWidth="6"
            strokeDasharray="180 30 70 25 100 18" pathLength="423" />
          <path d="M100 32 L98 60 L106 88 L94 120 L108 160 L100 168"
            {...stroke} strokeWidth="1.8" opacity="0.85" />
          <path d="M168 100 L140 96 L120 108 L100 100"
            {...stroke} strokeWidth="1.4" opacity="0.7" />
        </svg>
      );
    default:
      return null;
  }
}

export default function DojoSignature() {
  const [theme, setTheme] = useState(() => getThemeById(getStoredThemeId()));

  useEffect(() => {
    const handler = (e) => setTheme(e.detail);
    window.addEventListener("dojo-theme-changed", handler);
    return () => window.removeEventListener("dojo-theme-changed", handler);
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="dojo-signature"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 4,
        overflow: "hidden",
      }}
    >
      {/* Big kanji watermark — bottom-right, ghost-like */}
      <span
        key={`k-${theme.id}`}
        className="bc-kanji-bg"
        style={{
          position: "absolute",
          bottom: "-12vh",
          right: "-4vw",
          fontFamily: "'Noto Serif JP', 'Yu Mincho', 'Hiragino Mincho Pro', serif",
          fontWeight: 900,
          fontSize: "min(85vh, 90vw)",
          lineHeight: 0.85,
          color: theme.accent,
          opacity: 0.055,
          userSelect: "none",
          textShadow: `0 0 80px ${theme.glow}`,
          animation: "bcKanjiBreath 9s ease-in-out infinite",
        }}
      >
        {theme.kanji}
      </span>

      {/* Decorative SVG glyph — top-left, larger and softer */}
      <div
        key={`g-${theme.id}`}
        style={{
          position: "absolute",
          top: "-6vh",
          left: "-4vw",
          width: "min(55vh, 60vw)",
          height: "min(55vh, 60vw)",
          opacity: 0.07,
          filter: `drop-shadow(0 0 30px ${theme.glow})`,
          animation: "bcGlyphFloat 14s ease-in-out infinite",
        }}
      >
        <GlyphSvg kind={theme.glyph} color={theme.accent} />
      </div>
    </div>
  );
}
